import React, { useState } from 'react'
import {
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps
} from '@mui/material'

export interface OptionToggleOption {
  label: string
  value: string
}

interface OptionToggleProps<T extends string>
  extends Omit<ToggleButtonGroupProps, 'onChange' | 'value'> {
  options: (string | OptionToggleOption)[]
  defaultValue?: string
  onChange: (value: T) => void
  size?: 'small' | 'medium'
}

export const OptionToggle = React.forwardRef<
  HTMLDivElement,
  OptionToggleProps<any>
>(({ options, defaultValue, onChange, size = 'medium', ...props }, ref) => {
  const normalizedOptions = options.map((opt) =>
    typeof opt === 'string' ? { label: opt, value: opt } : opt
  )

  const [value, setValue] = useState(
    defaultValue || normalizedOptions[0]?.value || ''
  )

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: string | null
  ) => {
    if (newValue !== null) {
      setValue(newValue)
      onChange(newValue)
    }
  }

  return (
    <ToggleButtonGroup
      ref={ref}
      value={value}
      exclusive
      onChange={handleChange}
      sx={{
        backgroundColor: '#e8e8e8',
        borderRadius: '50px',
        padding: '4px',
        border: '1px solid #d0d0d0',
        display: 'flex',
        width: '100%',
        ...props.sx
      }}
      {...props}
    >
      {normalizedOptions.map((option) => (
        <ToggleButton
          key={option.value}
          value={option.value}
          sx={{
            flex: 1,
            minWidth: 0,
            px: size === 'small' ? 1 : 1.8,
            py: size === 'small' ? 0.6 : 0.9,
            fontSize: size === 'small' ? '0.8rem' : '1rem',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '50px',
            border: 'none',
            color: '#666',
            transition: 'all 0.3s ease',
            '&.Mui-selected': {
              backgroundColor: '#004bb1',
              color: '#fff',
              boxShadow: 'none',
              borderRadius: '50px',
              '&:hover': {
                backgroundColor: '#003a8a'
              }
            },
            '&:hover': {
              backgroundColor: '#f0f0f0'
            }
          }}
        >
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
})

OptionToggle.displayName = 'OptionToggle'
